import type {IncentiveTier} from 'Types/incentiveTier/incentiveTierTypes';
import {TierType} from 'Types/incentiveTier/incentiveTierTypes';
import {formatPercentage} from 'Utility/formatters/percentage';
import {isFreeSharesTier} from 'Store/deals/incentiveTiers/tierTypesUtils';

const formatFreeShares = (tier: IncentiveTier): string[] => {
  if (tier.freeShares && tier.freeShares > 0) {
    return [`${tier.freeShares} free shares`];
  }
  return [];
};

const formatDiscount = (tiers: IncentiveTier[]): string[] => {
  const discountList = tiers
    .filter(tier => tier.discountPercentage)
    .map(tier => `${formatPercentage(tier.discountPercentage)}%`)
    .join(' + ');

  if (discountList) {
    return [`${discountList} discount on the share price`];
  }
  return [];
};

const tierFormatters = {
  [TierType.TIME]: formatDiscount,
  [TierType.LOYALTY]: formatDiscount,
  [TierType.VOLUME]: formatDiscount,
  [TierType.FREE_SHARES]: formatFreeShares,
  [TierType.FREE_SHARES_CODES]: formatFreeShares,
} as const;

const getLeadingItems = (incentiveTier: IncentiveTier): string[] => {
  const formatter = tierFormatters[incentiveTier.tierType];
  if (formatter) {
    return formatter(incentiveTier);
  }
  return [];
};

export const formatMerchandiseList = (
  incentiveTiers: IncentiveTier | IncentiveTier[],
  excludeVolumeDiscount = false
): string[] => {
  const tierList = Array.isArray(incentiveTiers)
    ? incentiveTiers
    : incentiveTiers
    ? [incentiveTiers]
    : [];

  const groupedNonFreeSharesTiers = tierList.filter(
    tier =>
      tier.tierType === TierType.TIME ||
      tier.tierType === TierType.LOYALTY ||
      (!excludeVolumeDiscount && tier.tierType === TierType.VOLUME)
  );

  const freeSharesTiers = tierList.filter(tier =>
    isFreeSharesTier(tier?.tierType)
  );

  const freeSharesLeadingItems =
    freeSharesTiers.length > 0
      ? freeSharesTiers.map(tier => getLeadingItems(tier)).flat()
      : [];

  const nonFreeSharesTierLeadingItems =
    groupedNonFreeSharesTiers.length > 0
      ? formatDiscount(groupedNonFreeSharesTiers)
      : [];

  const merchandiseList = [
    ...new Set(tierList.flatMap(tier => tier.merchandiseList || [])),
  ];
  return [
    ...freeSharesLeadingItems,
    ...nonFreeSharesTierLeadingItems,
    ...merchandiseList,
  ];
};
